import { useEffect, useMemo, useState } from 'react';

import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Collapse,
  Grid,
  Group,
  Menu,
  Modal,
  Select,
  Stack,
  Text,
  TextInput
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconChevronDown,
  IconChevronRight,
  IconDotsVertical,
  IconGripVertical,
  IconPencil,
  IconPlus
} from '@tabler/icons-react';

import { useAppSelector } from '@/store/hooks';
import {
  useCreatePatientInsuranceMutation,
  useGetPatientInsurancesQuery,
  useGetPatientQuery,
  useReorderPatientInsurancesMutation,
  useUpdatePatientInsuranceMutation,
  useUpdatePatientMutation
} from '@/store/slices/api/patientsApiSlice';
import { formatUsZip, usZipError } from '@/utils/numericFields';

import {
  CreateInsuranceBody,
  HIERARCHY_LABEL,
  InsuranceHierarchy,
  InsurancePolicy,
  RELATIONSHIP_LABEL,
  sortPolicies,
  SubscriberRelationship
} from '@/types/insurance';

import EnrollmentGapIndicator from '../EnrollmentGapIndicator';
import InsuranceLookupModal from '../InsuranceLookupModal';

import { canEditFinancialOverride } from '../financialAssistance';

export const PAYER_OVERRIDES = [
  'P100',
  'P50',
  'SELF-PAY',
  'SELF-PAY50',
  'SELF-PAY100',
  'CCAP',
  'CCAP50',
  'CCAP100',
  'QMB'
] as const;

const RANK_ACCENT: Record<InsuranceHierarchy, string> = {
  primary: 'plum',
  secondary: 'teal',
  tertiary: 'blue',
  other: 'gray'
};

export type InsuranceFormState = {
  id?: string;
  hierarchy: InsuranceHierarchy;
  active: boolean;
  payerName: string;
  payerId: string;
  subscriberId: string;
  planName: string;
  groupId: string;
  subscriberRelationship: SubscriberRelationship;
  subscriberFirstName: string;
  subscriberLastName: string;
  subscriberDob: string;
  subscriberAddress1: string;
  subscriberCity: string;
  subscriberState: string;
  subscriberZip: string;
};

export const emptyInsuranceForm = (): InsuranceFormState => ({
  hierarchy: 'other',
  active: true,
  payerName: '',
  payerId: '',
  subscriberId: '',
  planName: '',
  groupId: '',
  subscriberRelationship: 'self',
  subscriberFirstName: '',
  subscriberLastName: '',
  subscriberDob: '',
  subscriberAddress1: '',
  subscriberCity: '',
  subscriberState: '',
  subscriberZip: ''
});

export function formFromPolicy(policy: InsurancePolicy): InsuranceFormState {
  return {
    id: policy.id,
    hierarchy: policy.hierarchy ?? 'other',
    active: policy.active,
    payerName: policy.payerName ?? '',
    payerId: policy.payerId ?? '',
    subscriberId: policy.subscriberId ?? '',
    planName: policy.planName ?? '',
    groupId: policy.groupId ?? '',
    subscriberRelationship: policy.subscriberRelationship ?? 'self',
    subscriberFirstName: policy.subscriberFirstName ?? '',
    subscriberLastName: policy.subscriberLastName ?? '',
    subscriberDob: policy.subscriberDob ?? '',
    subscriberAddress1: policy.subscriberAddress1 ?? '',
    subscriberCity: policy.subscriberCity ?? '',
    subscriberState: policy.subscriberState ?? '',
    subscriberZip: formatUsZip(policy.subscriberZip ?? '')
  };
}

export function insuranceBody(form: InsuranceFormState): CreateInsuranceBody {
  const notSelf = form.subscriberRelationship !== 'self';
  const text = (value: string) => value.trim() || null;
  return {
    hierarchy: form.hierarchy,
    active: form.active,
    payerName: text(form.payerName),
    payerId: text(form.payerId),
    subscriberId: text(form.subscriberId),
    planName: text(form.planName),
    groupId: text(form.groupId),
    subscriberRelationship: form.subscriberRelationship,
    subscriberFirstName: notSelf ? text(form.subscriberFirstName) : null,
    subscriberLastName: notSelf ? text(form.subscriberLastName) : null,
    subscriberDob: notSelf ? text(form.subscriberDob) : null,
    subscriberAddress1: notSelf ? text(form.subscriberAddress1) : null,
    subscriberCity: notSelf ? text(form.subscriberCity) : null,
    subscriberState: notSelf ? text(form.subscriberState) : null,
    subscriberZip: notSelf ? text(form.subscriberZip) : null,
    moveExistingToOther: form.active && form.hierarchy !== 'other'
  };
}

function errorMessage(error: unknown, fallback: string): string {
  const message = (error as { data?: { message?: string | string[] } })?.data?.message;
  if (Array.isArray(message)) return message.join(' ');
  if (typeof message === 'string' && message) return message;
  return fallback;
}

export const InsuranceModal = ({
  opened,
  initial,
  onClose,
  onSave,
  saving
}: {
  opened: boolean;
  initial: InsuranceFormState | null;
  onClose: () => void;
  onSave: (form: InsuranceFormState) => void;
  saving: boolean;
}) => {
  const [form, setForm] = useState<InsuranceFormState>(emptyInsuranceForm());
  const [openedFor, setOpenedFor] = useState<string | null>(null);
  const key = opened ? (initial?.id ?? 'new') : null;
  if (opened && key !== openedFor) {
    setOpenedFor(key);
    setForm(initial ? { ...initial } : emptyInsuranceForm());
  }
  if (!opened && openedFor) setOpenedFor(null);

  const set = <K extends keyof InsuranceFormState>(field: K, value: InsuranceFormState[K]) =>
    setForm((current) => ({ ...current, [field]: value }));

  const notSelf = form.subscriberRelationship !== 'self';
  const zipError = notSelf ? usZipError(form.subscriberZip) : '';
  const canSave =
    form.payerName.trim().length > 0 && form.subscriberId.trim().length > 0 && !zipError;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size="lg"
      radius="md"
      title={initial?.id ? 'Edit Insurance' : 'Add Insurance'}>
      <Stack gap="sm">
        <Group grow>
          <Select
            label="Coverage rank"
            data={[
              { value: 'primary', label: 'Primary' },
              { value: 'secondary', label: 'Secondary' },
              { value: 'tertiary', label: 'Tertiary' },
              { value: 'other', label: 'Other' }
            ]}
            value={form.hierarchy}
            onChange={(value) => value && set('hierarchy', value as InsuranceHierarchy)}
            allowDeselect={false}
          />
          <Select
            label="Status"
            data={[
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' }
            ]}
            value={form.active ? 'active' : 'inactive'}
            onChange={(value) => set('active', value === 'active')}
            allowDeselect={false}
          />
        </Group>
        <Group grow>
          <TextInput
            label="Payer Name"
            required
            value={form.payerName}
            onChange={(event) => set('payerName', event.currentTarget.value)}
          />
          <TextInput
            label="Payer Id"
            value={form.payerId}
            onChange={(event) => set('payerId', event.currentTarget.value)}
          />
        </Group>
        <Group grow>
          <TextInput
            label="Subscriber Id"
            required
            value={form.subscriberId}
            onChange={(event) => set('subscriberId', event.currentTarget.value)}
          />
          <TextInput
            label="Plan Name"
            value={form.planName}
            onChange={(event) => set('planName', event.currentTarget.value)}
          />
        </Group>
        <Group grow>
          <TextInput
            label="Group Id"
            value={form.groupId}
            onChange={(event) => set('groupId', event.currentTarget.value)}
          />
          <Select
            label="Subscriber Relationship"
            data={[
              { value: 'self', label: 'Self' },
              { value: 'spouse', label: 'Spouse' },
              { value: 'child', label: 'Child' },
              { value: 'other', label: 'Other' }
            ]}
            value={form.subscriberRelationship}
            onChange={(value) =>
              value && set('subscriberRelationship', value as SubscriberRelationship)
            }
            allowDeselect={false}
          />
        </Group>
        {notSelf && (
          <Box
            style={{
              border: '1px solid var(--mantine-color-gray-3)',
              borderRadius: 8,
              padding: 12,
              background: 'var(--mantine-color-gray-0)'
            }}>
            <Text size="xs" fw={600} c="dimmed" mb={8}>
              Subscriber (not the patient)
            </Text>
            <Stack gap="sm">
              <Group grow>
                <TextInput
                  label="First name"
                  value={form.subscriberFirstName}
                  onChange={(event) => set('subscriberFirstName', event.currentTarget.value)}
                />
                <TextInput
                  label="Last name"
                  value={form.subscriberLastName}
                  onChange={(event) => set('subscriberLastName', event.currentTarget.value)}
                />
              </Group>
              <TextInput
                label="Subscriber DOB"
                placeholder="YYYY-MM-DD"
                value={form.subscriberDob}
                onChange={(event) => set('subscriberDob', event.currentTarget.value)}
              />
              <TextInput
                label="Address"
                value={form.subscriberAddress1}
                onChange={(event) => set('subscriberAddress1', event.currentTarget.value)}
              />
              <Group grow>
                <TextInput
                  label="City"
                  value={form.subscriberCity}
                  onChange={(event) => set('subscriberCity', event.currentTarget.value)}
                />
                <TextInput
                  label="State"
                  value={form.subscriberState}
                  onChange={(event) => set('subscriberState', event.currentTarget.value)}
                />
                <TextInput
                  label="Zip"
                  inputMode="numeric"
                  value={form.subscriberZip}
                  error={zipError}
                  onChange={(event) => set('subscriberZip', formatUsZip(event.currentTarget.value))}
                />
              </Group>
            </Stack>
          </Box>
        )}
        <Group justify="flex-end" mt="xs">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button color="plum" disabled={!canSave} loading={saving} onClick={() => onSave(form)}>
            {initial?.id ? 'Save changes' : 'Add insurance'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

const PolicyCard = ({
  policy,
  onEdit,
  onRank,
  onToggleActive,
  draggable,
  canEdit
}: {
  policy: InsurancePolicy;
  onEdit: () => void;
  onRank: (hierarchy: InsuranceHierarchy) => void;
  onToggleActive: () => void;
  draggable?: boolean;
  canEdit: boolean;
}) => {
  const hierarchy = policy.hierarchy ?? 'other';
  const relationship = policy.subscriberRelationship ?? 'self';
  const fields = [
    { label: 'Subscriber Id', value: policy.subscriberId || '—' },
    { label: 'Payer Id', value: policy.payerId || '—' },
    { label: 'Plan Name', value: policy.planName || '—' },
    { label: 'Group Id', value: policy.groupId || '—' },
    { label: 'Subscriber Relationship', value: RELATIONSHIP_LABEL[relationship] }
  ];
  if (relationship !== 'self') {
    const name = [policy.subscriberFirstName, policy.subscriberLastName].filter(Boolean).join(' ');
    const address = [
      policy.subscriberAddress1,
      policy.subscriberCity,
      policy.subscriberState,
      policy.subscriberZip
    ]
      .filter(Boolean)
      .join(', ');
    fields.push(
      { label: 'Subscriber Name', value: name || '—' },
      { label: 'Subscriber DOB', value: policy.subscriberDob || '—' },
      { label: 'Subscriber Address', value: address || '—' }
    );
  }

  return (
    <Card withBorder radius="md" p="md" style={{ opacity: policy.active ? 1 : 0.7 }}>
      <Group justify="space-between" align="flex-start" wrap="nowrap" mb={8}>
        <Group gap={8} wrap="nowrap">
          {canEdit && draggable && (
            <IconGripVertical
              size={16}
              color="var(--mantine-color-gray-5)"
              style={{ cursor: 'grab', flexShrink: 0 }}
              aria-label="Drag to reorder"
            />
          )}
          <Text fw={600}>{policy.payerName || 'Unnamed payer'}</Text>
          <Badge size="sm" variant="light" color={RANK_ACCENT[hierarchy]} radius="sm">
            {HIERARCHY_LABEL[hierarchy]}
          </Badge>
          <Badge size="sm" variant="light" color={policy.active ? 'teal' : 'gray'} radius="sm">
            {policy.active ? 'Active' : 'Inactive'}
          </Badge>
        </Group>
        {canEdit && (
          <Group gap={4} wrap="nowrap">
            <ActionIcon variant="subtle" color="plum" onClick={onEdit} aria-label="Edit insurance">
              <IconPencil size={16} />
            </ActionIcon>
            <Menu position="bottom-end" withinPortal shadow="md">
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray" aria-label="More actions">
                  <IconDotsVertical size={16} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>Set rank</Menu.Label>
                <Menu.Item disabled={hierarchy === 'primary'} onClick={() => onRank('primary')}>
                  Make Primary
                </Menu.Item>
                <Menu.Item disabled={hierarchy === 'secondary'} onClick={() => onRank('secondary')}>
                  Make Secondary
                </Menu.Item>
                <Menu.Item disabled={hierarchy === 'tertiary'} onClick={() => onRank('tertiary')}>
                  Make Tertiary
                </Menu.Item>
                <Menu.Item disabled={hierarchy === 'other'} onClick={() => onRank('other')}>
                  Make Other
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item onClick={onToggleActive}>
                  {policy.active ? 'Mark inactive' : 'Reactivate'}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        )}
      </Group>
      <Grid>
        {fields.map((field) => (
          <Grid.Col key={field.label} span={{ base: 12, sm: 6, md: 4 }}>
            <Text size="sm" c="dimmed">
              {field.label}
            </Text>
            <Text size="sm" fw={600}>
              {field.value}
            </Text>
          </Grid.Col>
        ))}
      </Grid>
    </Card>
  );
};

const Insurance = ({ patientId }: { patientId: string }) => {
  const role = useAppSelector((state) => state.auth.user?.role);
  const isInternalUser = role?.includes('internal') ?? false;
  const canEditOverride = canEditFinancialOverride(role);
  const { data: patient } = useGetPatientQuery(patientId, { skip: !patientId });
  const [updatePatient, { isLoading: savingOverride }] = useUpdatePatientMutation();
  const savedOverride = patient?.financialPayerOverride ?? null;
  const [override, setOverride] = useState<string | null>(savedOverride);
  const { data: policies = [], isLoading } = useGetPatientInsurancesQuery(patientId);
  const [createInsurance, { isLoading: creating }] = useCreatePatientInsuranceMutation();
  const [updateInsurance, { isLoading: updating }] = useUpdatePatientInsuranceMutation();
  const [reorderInsurances] = useReorderPatientInsurancesMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [lookupOpen, setLookupOpen] = useState(false);
  const [editing, setEditing] = useState<InsuranceFormState | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);

  useEffect(() => {
    setOverride(savedOverride);
  }, [savedOverride]);

  const { active, inactive } = useMemo(() => {
    const sorted = sortPolicies(policies);
    return {
      active: sorted.filter((policy) => policy.active),
      inactive: sorted.filter((policy) => !policy.active)
    };
  }, [policies]);

  const notifyError = (error: unknown, fallback: string) => {
    notifications.show({
      title: 'Insurance',
      message: errorMessage(error, fallback),
      color: 'red'
    });
  };

  const handleOverride = async (value: string | null) => {
    const next = value ?? null;
    if (next === (savedOverride ?? null) || savingOverride) return;
    const previous = override;
    setOverride(next);
    try {
      await updatePatient({
        patient: { id: patientId, financialPayerOverride: next }
      }).unwrap();
    } catch (error) {
      setOverride(previous);
      notifyError(error, 'Could not update the financial override.');
    }
  };

  const handleSave = async (form: InsuranceFormState) => {
    try {
      if (form.id) {
        await updateInsurance({
          patientId,
          insuranceId: form.id,
          body: insuranceBody(form)
        }).unwrap();
      } else {
        await createInsurance({ patientId, body: insuranceBody(form) }).unwrap();
      }
      setModalOpen(false);
    } catch (error) {
      notifyError(error, 'Could not save this insurance.');
    }
  };

  const handleRank = async (policy: InsurancePolicy, hierarchy: InsuranceHierarchy) => {
    try {
      await updateInsurance({
        patientId,
        insuranceId: policy.id,
        body: {
          hierarchy,
          active: true,
          moveExistingToOther: hierarchy !== 'other'
        }
      }).unwrap();
    } catch (error) {
      notifyError(error, 'Could not change the coverage rank.');
    }
  };

  const handleToggleActive = async (policy: InsurancePolicy) => {
    const nextActive = !policy.active;
    try {
      await updateInsurance({
        patientId,
        insuranceId: policy.id,
        body: {
          active: nextActive,
          hierarchy: policy.hierarchy ?? 'other',
          moveExistingToOther: nextActive && policy.hierarchy !== 'other'
        }
      }).unwrap();
    } catch (error) {
      notifyError(error, 'Could not update this insurance.');
    }
  };

  const handleDrop = async (targetId: string) => {
    if (!dragId || dragId === targetId) {
      setDragId(null);
      return;
    }
    const ids = active.map((policy) => policy.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    setDragId(null);
    if (from === -1 || to === -1) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    try {
      await reorderInsurances({ patientId, insuranceIds: ids }).unwrap();
    } catch (error) {
      notifyError(error, 'Could not reorder insurance.');
    }
  };

  return (
    <Box>
      <Group justify="space-between" mb="sm" wrap="nowrap">
        <Group gap={6} wrap="nowrap">
          <Text fw={700}>Insurance</Text>
          <EnrollmentGapIndicator section="insurance" />
        </Group>
        {isInternalUser && (
          <Group gap="sm" wrap="nowrap" align="center">
            <Text size="xs" c="dimmed" fw={600} style={{ whiteSpace: 'nowrap' }}>
              Financial override
            </Text>
            {canEditOverride ? (
              <Select
                aria-label="Financial override"
                placeholder="None"
                data={PAYER_OVERRIDES.map((value) => ({ value, label: value }))}
                value={override}
                onChange={(value) => void handleOverride(value)}
                clearable
                allowDeselect={false}
                size="xs"
                w={156}
                disabled={savingOverride || !patient}
                comboboxProps={{ withinPortal: true }}
              />
            ) : (
              <Text size="xs" fw={600}>
                {savedOverride ?? 'None'}
              </Text>
            )}
            <Button
              size="xs"
              variant="light"
              color="plum"
              leftSection={<IconPlus size={16} />}
              onClick={() => setLookupOpen(true)}>
              Add & Edit Insurance
            </Button>
          </Group>
        )}
      </Group>

      {isLoading ? (
        <Text size="sm" c="dimmed">
          Loading insurance…
        </Text>
      ) : active.length === 0 ? (
        <Text size="sm" c="dimmed" mb="md">
          No active insurance on file.
        </Text>
      ) : (
        <>
          {isInternalUser && active.length > 1 && (
            <Text size="xs" c="dimmed" mb={6}>
              Drag to reorder. The first is Primary, then Secondary, then Tertiary. The rest become
              Other.
            </Text>
          )}
          <Stack gap="sm">
            {active.map((policy) => (
              <Box
                key={policy.id}
                draggable={isInternalUser && active.length > 1}
                onDragStart={() => isInternalUser && setDragId(policy.id)}
                onDragOver={(event) => isInternalUser && event.preventDefault()}
                onDrop={() => isInternalUser && void handleDrop(policy.id)}
                style={{ opacity: dragId === policy.id ? 0.5 : 1 }}>
                <PolicyCard
                  policy={policy}
                  canEdit={isInternalUser}
                  draggable={isInternalUser && active.length > 1}
                  onEdit={() => {
                    setEditing(formFromPolicy(policy));
                    setModalOpen(true);
                  }}
                  onRank={(hierarchy) => void handleRank(policy, hierarchy)}
                  onToggleActive={() => void handleToggleActive(policy)}
                />
              </Box>
            ))}
          </Stack>
        </>
      )}

      {inactive.length > 0 && (
        <Box mt="lg">
          <Group
            gap={6}
            mb="sm"
            style={{ cursor: 'pointer', width: 'fit-content' }}
            onClick={() => setShowInactive((open) => !open)}>
            {showInactive ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
            <Text fw={600} size="sm" c="dimmed">
              Inactive insurance ({inactive.length})
            </Text>
          </Group>
          <Collapse in={showInactive}>
            <Stack gap="sm">
              {inactive.map((policy) => (
                <PolicyCard
                  key={policy.id}
                  policy={policy}
                  canEdit={isInternalUser}
                  onEdit={() => {
                    setEditing(formFromPolicy(policy));
                    setModalOpen(true);
                  }}
                  onRank={(hierarchy) => void handleRank(policy, hierarchy)}
                  onToggleActive={() => void handleToggleActive(policy)}
                />
              ))}
            </Stack>
          </Collapse>
        </Box>
      )}

      <InsuranceModal
        opened={modalOpen}
        initial={editing}
        onClose={() => setModalOpen(false)}
        onSave={(form) => void handleSave(form)}
        saving={creating || updating}
      />
      <InsuranceLookupModal
        opened={lookupOpen}
        onClose={() => setLookupOpen(false)}
        patientId={patientId}
      />
    </Box>
  );
};

export default Insurance;
